<template>
	<div
		class="app-select"
		:class="{
			'app-select--open': isOpen,
			'app-select--disabled': disabled,
			'app-select--multiple': multiple,
			'app-select--focused': isFocused,
			'app-select--has-groups': hasGroups,
			'app-select--searchable': searchable && !multiple && !ownerChoise,
			'app-select--clearable': clearable
		}"
		v-click-outside="closeDropdown"
	>
		<div
			ref="triggerRef"
			class="app-select__trigger"
			:class="{
				'app-select__trigger--editable': searchable && !multiple && !ownerChoise && isOpen
			}"
			:style="customTriggerStyle"
			:tabindex="disabled ? -1 : 0"
			@click="handleTriggerClick"
			@keydown.enter.prevent="handleTriggerEnter"
			@keydown.esc="closeDropdown"
			@keydown.down.prevent="handleTriggerKeyDown"
			@keydown.up.prevent="handleTriggerKeyDown"
			@focus="isFocused = true"
			@blur="isFocused = false"
		>
			<div
				class="app-select__values"
				:class="{
					'app-select__values--hidden': searchable && !multiple && !ownerChoise && isOpen
				}"
			>
				<template v-if="multiple && dropdownLabel==undefined">
					<template v-if="selectedDisplayItems.length">
						<span class="app-select__first-value">
							{{ selectedDisplayItems[0].label }}
						</span>
						<span
							v-if="selectedDisplayItems.length > 1"
							class="app-select__additional-count"
						>
							+{{ selectedDisplayItems.length - 1 }}
						</span>
					</template>
					<span
						v-else
						class="app-select__placeholder"
					>
						{{ placeholder }}
					</span>
				</template>

				<template v-else-if="dropdownLabel">
					<template v-if="selectedDisplayItems.length">
						<span class="app-select__first-value">
							{{ dropdownLabel }}
						</span>
					</template>
					<span
						v-else
						class="app-select__placeholder"
					>
						{{ placeholder }}
					</span>
				</template>

				<template v-else>
					<span
						v-if="selectedValue != null"
						class="app-select__single-value"
					>
						{{ getDisplayValue(selectedValue) }}
					</span>
					<span
						v-else
						class="app-select__placeholder"
					>
						{{ placeholder }}
					</span>
				</template>
			</div>
			<div
				v-if="searchable && !multiple && !ownerChoise && isOpen"
				class="app-select__edit-container"
				@click.stop
			>
				<input
					ref="editInputRef"
					v-model="searchQuery"
					class="app-select__edit-input"
					:placeholder="searchPlaceholder"
					@keydown.enter.prevent="handleSearchEnter"
					@keydown.down.prevent="handleDropdownKeyDown"
					@keydown.up.prevent="handleDropdownKeyDown"
				/>
			</div>

			<div class="app-select__actions" @click.stop>
				<app-icon
					v-if="clearable && selectedValuesLength > 0"
					class="app-select__clear-button"
					name="arrow-left"
					width="16px"
					color="#979797"
					@click.stop="handleClear"
				/>

				<slot name="append-icon" :close="closeDropdown">
					<app-icon
					v-if="!disabled"
						class="app-select__arrow"
						name="chevron-down"
						width="16px"
						color="#979797"
						cursor="pointer"
						@click.stop="toggleDropdown"
					/>
					<app-icon
					v-else
						class="app-select__arrow"
						name="lock"
						width="16px"
						color="#979797"
						cursor="pointer"
						@click.stop="toggleDropdown"
					/>
				</slot>
			</div>
		</div>

		<Teleport to="body">
			<transition name="select-dropdown">
				<div
					v-if="isOpen"
					:class="[
						'app-select__dropdown',
						dropdownClass,
						{ 'app-select__dropdown--single': !multiple }
					]"
					:style="dropdownStyles"
					ref="dropdownRef"
					@keydown.enter.prevent="handleDropdownEnter"
					@keydown.esc="closeDropdown"
					@keydown.down.prevent="handleDropdownKeyDown"
					@keydown.up.prevent="handleDropdownKeyDown"
				>
					<div :style="customSearchStyle">
						<div
							v-if="searchable && (multiple || ownerChoise)"
							class="app-select__search"
						>
							<app-input
								ref="$searchInputRef"
								v-model="searchQuery"
								class="app-select__search-input"
								:placeholder="searchPlaceholder"
								show-search-button
								@click.stop
							/>
						</div>
						<div
							v-if="multiple"
							class="app-select__controls"
							@click.stop
						>
							<div class="app-select__controls-left">
								<app-checkbox
									:value="showOnlySelected"
									@on-input="toggleShowOnlySelected"
									@click.stop
								/>
								<span class="app-select__controls-text">Показать выбранных</span>
							</div>
							<div class="app-select__controls-right">
								<button
									class="app-select__reset-button"
									@click.stop="resetSelection"
									:disabled="!selectedValues.length"
								>
									Сбросить
								</button>
							</div>
						</div>
					</div>
					<div class="app-select__options-container">
						<slot
							name="dropdown"
							:items="filteredItemsWithSelected"
							:selected="selectedValues"
							:select="handleSelect"
							:getGroupState="getGroupState"
							:close="closeDropdown"
							:search="searchQuery"
						>
							<div class="app-select__options">
								<template v-if="multiple && showSelectAll && selectableItems.length">
									<div class="app-select__option app-select__option--select-all">
										<div class="app-select__option-label" @click.stop>
											<app-checkbox
												:value="isAllSelected"
												@on-input="toggleAll"
											/>
											<span
												class="app-select__group-title"
												:style="{ fontWeight: '400' }"
											>
												Все пользователи
											</span>
										</div>
									</div>
								</template>
								<template
									v-for="item in filteredItemsWithSelected"
									:key="String(item.id)"
								>
									<div
										v-if="item.type === 'group'"
										class="app-select__group"
									>
										<div
											class="app-select__group-header"
											:class="{
												'app-select__group-header--selectable': multiple && item.selectable !== false,
												'app-select__group-header--disabled': item.disabled,
												'app-select__group-header--focused': focusedValue === item.id
											}"
											@click.stop="toggleGroupSelection(item)"
											@mouseenter="focusedValue = item.id"
										>
											<label
												v-if="multiple && item.selectable !== false"
												class="app-select__group-checkbox"
												@click.stop
											>
												<app-checkbox
													:value="getGroupState(item).isSelected"
													:disabled="item.disabled"
													@on-input="toggleGroupSelection(item)"
												/>
											</label>

											<span
												class="app-select__group-title"
												v-html="highlightSearchText(searchQuery, item.label)"
											></span>
											<span class="app-select__group-count">
												({{
													findOriginalGroup(item.id)?.children.length || item.children.length
												}})
											</span>
										</div>
										<div v-if="item.showChildren" class="app-select__group-children">
											<div
												v-for="child in item.children"
												:key="String(child.id)"
												class="app-select__option app-select__option--child"
												:class="{
													'app-select__option--disabled': child.disabled,
													'app-select__option--focused': focusedValue === child.id
												}"
												@click="handleItemClick(child)"
												@mouseenter="focusedValue = child.id"
											>
												<template v-if="multiple">
													<label class="app-select__option-label" @click.stop>
														<app-checkbox
															:value="isSelected(child.id)"
															:disabled="child.disabled"
															@on-change="handleSelect(child.id, $event)"
														/>
														<span
															v-html="highlightSearchText(searchQuery, child.label)"></span>
													</label>
												</template>
												<template v-else>
													<span v-html="highlightSearchText(searchQuery, child.label)"></span>
												</template>
											</div>
										</div>
									</div>
									<div
										v-else
										class="app-select__option"
										:class="{
											'app-select__option--selected': isSelected(item.id),
											'app-select__option--disabled': item.disabled,
											'app-select__option--focused': focusedValue === item.id
										}"
										@click="handleItemClick(item)"
										@mouseenter="focusedValue = item.id"
									>
										<template v-if="multiple">
											<label class="app-select__option-label" @click.stop>
												<app-checkbox
													:value="isSelected(item.id)"
													:disabled="item.disabled"
													@on-change="handleSelect(item.id, $event)"
												/>
												<span v-html="highlightSearchText(searchQuery, item.label)"></span>
											</label>
										</template>
										<template v-else>
											<span v-html="highlightSearchText(searchQuery, item.label)"></span>
										</template>
									</div>
								</template>

								<div
									v-if="!filteredItemsWithSelected.length"
									class="app-select__empty"
								>
									{{
										searchQuery ? 'Подходящих пользователей не найдено' : showOnlySelected ? 'Нет выбранных пользователей' : 'Нет данных'
									}}
								</div>
							</div>
						</slot>
					</div>
				</div>
			</transition>
		</Teleport>
	</div>
</template>

<script setup lang="ts">
import {
	ref,
	computed,
	watch,
	nextTick,
	onUnmounted
} from 'vue'
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import { highlightSearchText } from "@/utils/textHelpers";

export interface SelectItem {
	id: string | number
	label: string
	type?: 'item' | 'group'
	disabled?: boolean
	[key: string]: any
}

export interface SelectGroup extends SelectItem {
	type: 'group'
	children: SelectItem[]
	selectable?: boolean
}

export type SelectOption = SelectItem | SelectGroup
export type SelectModelValue = string | number | (string | number)[] | null

type Props = {
	modelValue?: SelectModelValue
	items?: SelectOption[]
	multiple?: boolean
	dropdownLabel?: string
	placeholder?: string
	disabled?: boolean
	showSelectAll?: boolean
	valueKey?: string
	labelKey?: string
	displayKey?: string
	searchable?: boolean
	searchPlaceholder?: string
	closeOnSelect?: boolean
	clearable?: boolean,
	minDropdownWidth?: string | number
	dropdownHeight?: string | number
	maxDropdownHeight?: string | number
	customTriggerStyle?: any;
	customSearchStyle?: any;
	dropdownClass?: string;
	ownerChoise?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	modelValue: null,
	items: () => [],
	multiple: false,
	placeholder: 'Выберите...',
	disabled: false,
	showSelectAll: false,
	valueKey: 'id',
	labelKey: 'label',
	displayKey: undefined,
	searchable: false,
	searchPlaceholder: 'Поиск...',
	closeOnSelect: true,
	clearable: false,
	dropdownHeight: undefined,
	maxDropdownHeight: 400,
	minTriggerHeight: 32
})

const emit = defineEmits<{
	(e: 'update:modelValue', value: SelectModelValue): void
	(e: 'select', value: SelectModelValue): void
	(e: 'open'): void
	(e: 'close'): void
	(e: 'search', value: string): void
}>()

const isOpen = ref(false)
const isFocused = ref(false)
const focusedValue = ref<string | number | null>(null)
const searchQuery = ref('')
const triggerRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)
const $searchInputRef = ref<typeof AppInput | null>(null)
const editInputRef = ref<HTMLInputElement | null>(null)
const showOnlySelected = ref(false)
const dropdownStyles = ref({})

// Проверка наличия групп
const hasGroups = computed(() => {
	return props.items.some(item => item.type === 'group')
})

// Получение всех выбираемых элементов
const selectableItems = computed(() => {
	const result: { id: string | number; label: string }[] = []

	props.items.forEach(item => {
		if (item.type === 'group') {
			item.children.forEach(child => {
				if (!child.disabled) {
					result.push({ id: child.id, label: child.label })
				}
			})
		} else {
			if (!item.disabled) {
				result.push({ id: item.id, label: item.label })
			}
		}
	})
	return result
})

// Поиск оригинальной группы для получения полного количества детей
const findOriginalGroup = (groupId: string | number) => {
	return props.items.find(item =>
		item.type === 'group' && item.id === groupId
	) as SelectGroup | undefined
}

// Фильтрация при поиске
const processedItems = computed((): SelectOption[] => {
	if (!props.searchable || !searchQuery.value) {
		return props.items.map(item => {
			if (item.type === 'group') {
				return {
					...item,
					showChildren: true
				}
			}
			return item
		})
	}

	return props.items.reduce<SelectOption[]>((acc, item) => {
		if (item.type === 'group') {
			const groupMatches = item.label.toLowerCase().includes(searchQuery.value.toLowerCase())

			if (groupMatches) {
				acc.push({
					...item,
					showChildren: true
				})
			} else {
				const matchingChildren = item.children.filter(child =>
					child.label.toLowerCase().includes(searchQuery.value.toLowerCase())
				)
				if (matchingChildren.length > 0) {
					acc.push({
						...item,
						children: matchingChildren,
						showChildren: true
					})
				}
			}
		} else {
			if (item.label.toLowerCase().includes(searchQuery.value.toLowerCase())) {
				acc.push(item)
			}
		}

		return acc
	}, [])
})

// Отфильтрованные выбираемые элементы
const filteredSelectableItems = computed(() => {
	if (!props.searchable || !searchQuery.value) {
		return selectableItems.value
	}

	return selectableItems.value.filter(item =>
		item.label.toLowerCase().includes(searchQuery.value.toLowerCase())
	)
})

// Выбранные значения
const selectedValue = computed(() => {
	if (props.multiple) return null
	return props.modelValue
})

const selectedValues = computed((): (string | number)[] => {
	if (!props.multiple) return []
	return Array.isArray(props.modelValue) ? props.modelValue : []
})

// Фильтрация по выбранным элементам
const filteredItemsWithSelected = computed((): SelectOption[] => {
	if (!showOnlySelected.value || !props.multiple) {
		return processedItems.value
	}

	return processedItems.value.reduce<SelectOption[]>((acc, item) => {
		if (item.type === 'group') {
			const selectedChildren = item.children.filter(child =>
				selectedValues.value.includes(child.id)
			)

			if (selectedChildren.length > 0) {
				acc.push({
					...item,
					children: selectedChildren,
					showChildren: item.showChildren
				})
			}
		} else {
			if (selectedValues.value.includes(item.id)) {
				acc.push(item)
			}
		}
		return acc
	}, [])
})

// Количество выбранных элементов
const selectedValuesLength = computed(() => {
	if (props.multiple) {
		return selectedValues.value.length
	}
	return selectedValue.value != null ? 1 : 0
})

// Состояние группы
const getGroupState = (group: SelectGroup) => {
	const childIds = group.children.map(child => child.id)
	const hasDisabledChildren = group.children.some(child => child.disabled)

	return {
		isSelected: childIds.length > 0 && childIds.every(id => selectedValues.value.includes(id)),
		isDisabled: group.disabled || hasDisabledChildren
	}
}

// Проверка выбран ли элемент
const isSelected = (value: string | number): boolean => {
	if (props.multiple) {
		return selectedValues.value.includes(value)
	}
	return selectedValue.value === value
}

// Получение отображаемого текста
const getDisplayValue = (value: string | number): string => {
	const label = findDisplayItem(props.items, value)
	return label || String(value)
}

const findDisplayItem = (items: SelectOption[], value: string | number): string | undefined => {
	for (const item of items) {
		if (item.type === 'group') {
			if (item.id === value) return item.label
			const found = item.children.find(child => child.id === value)
			if (found) return found.label
		} else {
			if (item.id === value) return item.label
		}
	}
	return undefined
}

// Элементы для отображения в триггере
const selectedDisplayItems = computed(() => {
	return selectedValues.value.map(id => ({
		id,
		label: getDisplayValue(id)
	}))
})

// Проверка выбраны ли все
const isAllSelected = computed((): boolean => {
	if (!props.multiple) return false
	return selectableItems.value.length > 0 &&
		selectableItems.value.every(item => selectedValues.value.includes(item.id))
})

// Обновление позиции дропдауна
const updateDropdownPosition = () => {
	if (!triggerRef.value || !isOpen.value) return

	const rect = triggerRef.value.getBoundingClientRect()
	const viewportHeight = window.innerHeight
	const spaceBelow = viewportHeight - rect.bottom
	const spaceAbove = rect.top

	// Определяем направление открытия
	let openUp = false
	if (props.dropdownHeight === undefined) {
		// При динамической высоте используем старую логику
		openUp = spaceBelow < 300 && spaceAbove > spaceBelow
	} else {
		// При фиксированной высоте тоже можно определять направление,
		// чтобы дропдаун не выходил за экран по вертикали
		openUp = spaceBelow < (typeof props.dropdownHeight === 'number' ? props.dropdownHeight : parseFloat(props.dropdownHeight)) && spaceAbove > spaceBelow
	}

	// Вычисляем ширину (без изменений)
	let width = `${rect.width}px`
	if (props.minDropdownWidth) {
		const minWidth = typeof props.minDropdownWidth === 'number'
			? `${props.minDropdownWidth}px`
			: props.minDropdownWidth
		width = `max(${minWidth}, ${rect.width}px)`
	}

	// Базовые стили позиционирования
	const styles: any = {
		position: 'fixed',
		top: openUp ? 'auto' : `${rect.bottom + 4}px`,
		bottom: openUp ? `${viewportHeight - rect.top + 4}px` : 'auto',
		left: `${rect.left}px`,
		width: width,
		minWidth: props.minDropdownWidth ? `${rect.width}px` : 'auto',
		zIndex: 10000
	}

	if (props.dropdownHeight !== undefined) {
		// Фиксированная высота
		const heightValue = typeof props.dropdownHeight === 'number'
			? `${props.dropdownHeight}px`
			: props.dropdownHeight
		styles.height = heightValue
		// Убираем maxHeight, чтобы не конфликтовать
		styles.maxHeight = 'none'
	} else {
		// Динамическая высота
		const availableHeight = openUp ? spaceAbove - 20 : spaceBelow - 20
		let maxH = Math.max(100, availableHeight)
		if (props.maxDropdownHeight !== undefined) {
			const limit = typeof props.maxDropdownHeight === 'number'
				? props.maxDropdownHeight
				: parseFloat(props.maxDropdownHeight)
			if (!Number.isNaN(limit)) {
				maxH = Math.min(maxH, limit)
			}
		}
		styles.maxHeight = `${maxH}px`
	}

	dropdownStyles.value = styles
}

// const minTriggerHeight =computed(() => {
// 	const styles: any = {
// 		minHeight:`32px`,
// 	}

// 	if (props.minTriggerHeight !== 32) {
// 		styles.minHeight = `${typeof props.minTriggerHeight === 'number'
// 			? props.minTriggerHeight
// 			: parseFloat(props.minTriggerHeight)}px`
// 	}

// 	return styles;
// });

// Сброс выбора
const resetSelection = (): void => {
	if (!props.multiple) return

	emit('update:modelValue', [])
	emit('select', [])
}

const toggleShowOnlySelected = (value: boolean) => {
	showOnlySelected.value = value
}

// Очистка значения
const handleClear = () => {
	if (props.multiple) {
		emit('update:modelValue', [])
		emit('select', [])
	} else {
		emit('update:modelValue', null)
		emit('select', null)
	}
}

// Обработка клика по триггеру
const handleTriggerClick = () => {
	if (props.disabled) return

	toggleDropdown()
}

// Обработка Enter на триггере
const handleTriggerEnter = (): void => {
	if (props.disabled) return

	if (!isOpen.value) {
		toggleDropdown()
	} else {
		nextTick(() => {
			if (dropdownRef.value) {
				dropdownRef.value.focus()
			}
		})
	}
}

// Обработка Enter в поиске
const handleSearchEnter = () => {
	if (!isOpen.value) return;

	if (focusedValue.value) {
		const isInList = filteredSelectableItems.value.some(
			item => item.id === focusedValue.value
		);

		if (isInList) {
			handleSelect(focusedValue.value);
			closeDropdown();
			return;
		}
	}

	const firstItem = filteredSelectableItems.value[0];
	if (firstItem) {
		handleSelect(firstItem.id);
		closeDropdown();
	}
};

// Обработка Enter в дропдауне
const handleDropdownEnter = (): void => {
	if (!isOpen.value || !focusedValue.value) return

	const isInFilteredList = filteredSelectableItems.value.some(
		item => item.id === focusedValue.value
	)

	if (!isInFilteredList) return

	const item = findEnterPressedItem(processedItems.value)
	if (item && !item.disabled) {
		if (item.type !== 'group') {
			handleSelect(item.id)
		}
	}
}

const findEnterPressedItem = (items: SelectOption[]): SelectItem | undefined => {
	for (const item of items) {
		if (item.type === 'group') {
			if (item.id === focusedValue.value) {
				if (props.multiple && (item as SelectGroup).selectable) {
					toggleGroupSelection(item as SelectGroup)
					return undefined
				}
				return undefined
			}
			const found = item.children.find(child => child.id === focusedValue.value)
			if (found) return found
		} else {
			if (item.id === focusedValue.value) return item
		}
	}
	return undefined
}

// Обработка клавиш на триггере
const handleTriggerKeyDown = (event: KeyboardEvent): void => {
	if (!isOpen.value) {
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault()
			toggleDropdown()
		}
	}
}

// Обработка клавиш в дропдауне
const handleDropdownKeyDown = (event: KeyboardEvent): void => {
	if (!isOpen.value || !filteredSelectableItems.value.length) return

	const currentIndex = filteredSelectableItems.value.findIndex(
		item => item.id === focusedValue.value
	)

	let newIndex: number

	switch (event.key) {
		case 'ArrowDown':
			event.preventDefault()
			newIndex = currentIndex < filteredSelectableItems.value.length - 1 ? currentIndex + 1 : 0
			focusedValue.value = filteredSelectableItems.value[newIndex].id
			nextTick(() => scrollToFocused())
			break

		case 'ArrowUp':
			event.preventDefault()
			newIndex = currentIndex > 0 ? currentIndex - 1 : filteredSelectableItems.value.length - 1
			focusedValue.value = filteredSelectableItems.value[newIndex].id
			nextTick(() => scrollToFocused())
			break
	}
}

// Обработка выбора элемента
const handleSelect = (value: string | number, event?: Event): void => {
	if (props.disabled) return

	if (event) {
		event.stopPropagation()
	}

	let newValue: SelectModelValue

	if (props.multiple) {
		const values = [...selectedValues.value]
		const index = values.indexOf(value)

		if (index === -1) {
			values.push(value)
		} else {
			values.splice(index, 1)
		}

		newValue = values
	} else {
		newValue = value
		if (props.closeOnSelect) {
			setTimeout(() => {
				isOpen.value = false
				emit('close')
			}, 100)
		}
	}

	emit('update:modelValue', newValue)
	emit('select', newValue)

	if (props.searchable && !props.multiple) {
		searchQuery.value = ''
	}
}

// Обработка клика по элементу
const handleItemClick = (item: SelectItem): void => {
	if (item.disabled || item.type === 'group') return

	if (!props.multiple) {
		handleSelect(item.id)
	}
}

// Переключение выбора группы
const toggleGroupSelection = (group: SelectGroup): void => {
	if (!props.multiple || group.selectable === false || group.disabled) return

	const originalGroup = findOriginalGroup(group.id)
	if (!originalGroup) return

	const childIds = originalGroup.children
		.filter(child => !child.disabled)
		.map(child => child.id)

	const state = getGroupState(originalGroup)
	const currentValues = [...selectedValues.value]

	let newValues: (string | number)[]

	if (state.isSelected) {
		newValues = currentValues.filter(id => !childIds.includes(id))
	} else {
		newValues = [...currentValues]
		childIds.forEach(id => {
			if (!newValues.includes(id)) {
				newValues.push(id)
			}
		})
	}

	emit('update:modelValue', newValues)
	emit('select', newValues)

	if (props.searchable && props.multiple) {
		searchQuery.value = ''
		const firstSelectable = filteredSelectableItems.value[0]
		focusedValue.value = firstSelectable?.id || null
	}
}

// Выбрать все
const toggleAll = (): void => {
	if (!props.multiple) return

	if (isAllSelected.value) {
		emit('update:modelValue', [])
		emit('select', [])
	} else {
		emit('update:modelValue', selectableItems.value.map(item => item.id))
		emit('select', selectableItems.value.map(item => item.id))
	}

	if (props.searchable && props.multiple) {
		searchQuery.value = ''
		const firstSelectable = filteredSelectableItems.value[0]
		focusedValue.value = firstSelectable?.id || null
	}
}

// Переключение дропдауна
const toggleDropdown = (): void => {
	if (props.disabled) return

	isOpen.value = !isOpen.value

	if (isOpen.value) {
		emit('open')
		updateDropdownPosition()

		const firstSelectable = filteredSelectableItems.value[0]
		focusedValue.value = firstSelectable?.id || null

		nextTick(() => {
			if (props.searchable) {
				if ((props.multiple || props.ownerChoise) && $searchInputRef.value) {
					$searchInputRef.value.focusInput()
				} else if (!props.multiple && editInputRef.value) {
					editInputRef.value.focus()
				}
			} else if (dropdownRef.value) {
				dropdownRef.value.focus()
			}
		})
	} else {
		emit('close')
		if (props.multiple) {
			searchQuery.value = ''
		}
	}
}

// Закрытие дропдауна
const closeDropdown = (): void => {
	if (isOpen.value) {
		isOpen.value = false
		emit('close')
		if (!props.multiple) {
			searchQuery.value = ''
		}
		// triggerRef.value?.focus()
		triggerRef.value?.blur()
	}
}

// Скролл к фокусированному элементу
const scrollToFocused = (): void => {
	if (!dropdownRef.value || !focusedValue.value) return

	const focusedElement = dropdownRef.value.querySelector(
		'.app-select__option--focused, .app-select__group-header--focused'
	) as HTMLElement

	if (focusedElement) {
		focusedElement.scrollIntoView({
			block: 'center',
			behavior: 'smooth'
		})
	}
}

// Обработка Escape
const handleEscape = (event: KeyboardEvent) => {
	if (event.key === 'Escape') {
		event.preventDefault()
		event.stopPropagation()
		if (isOpen.value) {
			closeDropdown()
		}
	}
}

watch(searchQuery, (value) => {
	emit('search', value)
	const firstSelectable = filteredSelectableItems.value[0]
	focusedValue.value = firstSelectable?.id || null
})

const handleClickOutside = (e: MouseEvent) => {
  const target = e.target as Node
  if (
    triggerRef.value?.contains(target) ||
    dropdownRef.value?.contains(target)
  ) return
  closeDropdown()
}

watch(isOpen, (newVal) => {
	if (newVal) {
		// document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('keydown', handleEscape, { capture: true })
		window.addEventListener('scroll', updateDropdownPosition, true)
		window.addEventListener('resize', updateDropdownPosition)
	} else {
		// document.removeEventListener('mousedown', handleClickOutside)
		document.removeEventListener('keydown', handleEscape, { capture: true })
		window.removeEventListener('scroll', updateDropdownPosition, true)
		window.removeEventListener('resize', updateDropdownPosition)
		showOnlySelected.value = false
		if (props.multiple) {
			searchQuery.value = ''
		}
	}
})

onUnmounted(() => {
	document.removeEventListener('keydown', handleEscape, { capture: true })
	window.removeEventListener('scroll', updateDropdownPosition, true)
	window.removeEventListener('resize', updateDropdownPosition)
})
</script>

<style lang="scss" scoped>
@import "@/styles/color.scss";

.app-select {
	position: relative;
	width: 100%;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
	font-size: 14px;

	&__trigger {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 5px 12px;
		border: 1px solid #D2D2D2;
		border-radius: 8px;
		background-color: #FFF;
		cursor: pointer;
		min-height: 32px;
		transition: border-color 0.2s ease;

		&:hover {
			border-color: #3F884F;
		}

		&:focus {
			outline: none;
			border-color: #3F884F;
		}

		&--editable {
			cursor: text;
			padding: 4px 12px;
		}
	}

	&--searchable &__trigger {
		cursor: text;
	}

	&--open &__trigger {
		border-color: #3F884F;
	}

	&--focused &__trigger {
		border-color: #3F884F;
	}

	&--disabled &__trigger {
		// background-color: #f5f5f5;
		cursor: not-allowed;
		// color: rgba(0, 0, 0, 0.25);
		border-color: #D2D2D2;

		&:hover {
			border-color: #D2D2D2;
		}
	}

	&__values {
		flex: 1;
		display: flex;
		align-items: center;
		gap: 4px;
		min-height: 20px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		transition: opacity 0.2s ease;

		&--hidden {
			opacity: 0;
			pointer-events: none;
		}
	}

	&__first-value {
		font-size: 14px;
		line-height: 17px;
		font-weight: 400;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		color: $color-text_main;
	}

	&__additional-count {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0 3px;
		background-color: rgba(#3F884F, 0.2);
		color: #333;
		border-radius: 4px;
		font-size: 12px;
		line-height: 16px;
		font-weight: 500;
		margin-left: 4px;
	}

	&__single-value {
		font-size: 14px;
		line-height: 18px;
		color: $color-text_main;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__placeholder {
		color: #D2D2D2;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__edit-container {
		position: absolute;
		top: 0;
		left: 0;
		right: 40px;
		bottom: 0;
		display: flex;
		align-items: center;
		padding: 0 12px;
		background: transparent;
		z-index: 2;
	}

	&__edit-input {
		width: 100%;
		border: none;
		outline: none;
		font-size: 14px;
		line-height: 18px;
		color: #333;
		background: transparent;
		padding: 0;

		&::placeholder {
			color: #D2D2D2;
		}
	}

	&__actions {
		display: flex;
		align-items: center;
		gap: 4px;
		margin-left: 8px;
		position: relative;
		z-index: 3;
	}

	&__clear-button {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0;
		border: none;
		background: none;
		cursor: pointer;
		opacity: 0.6;
		transition: opacity 0.2s ease;

		&:hover {
			opacity: 1;
		}

		&:disabled {
			cursor: not-allowed;
			opacity: 0.3;
		}
	}

	&__arrow {
		transition: transform 0.2s ease;
		cursor: pointer;
	}

	&--open &__arrow {
		transform: rotate(180deg);
	}

	&__dropdown {
		display: flex;
		flex-direction: column;
		background-color: #FFF;
		border-radius: 4px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		overflow: hidden;

		&:focus {
			outline: none;
		}

		&--single .app-select__option--selected {
			color: #499259;
		}
	}

	&__search {
		padding: 12px 12px 8px;
		background-color: #FFF;
	}

	&__search-input {
		width: 100%;
	}

	&__controls {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0 12px 8px;
		background-color: #FFF;

		&-left {
			display: flex;
			align-items: center;
		}

		&-text {
			font-size: 14px;
			line-height: 18px;
			color: $color-text_main;
			margin-left: 8px;
		}

		&-right {
			display: flex;
			align-items: center;
		}
	}

	&__reset-button {
		background: none;
		border: none;
		color: #979797;
		font-size: 14px;
		cursor: pointer;
		padding: 4px 8px;

		&:hover {
			color: #3F884F;
			text-decoration: underline;
		}

		&:disabled {
			color: #D2D2D2;
			cursor: not-allowed;
			text-decoration: none;
		}
	}

	&__options-container {
		// overflow-y: auto;
		// max-height: 450px;
		overflow-y: auto;
		flex: 1;
	}

	&__options {
		padding: 4px;
	}

	&__group {
		margin: 2px 0 2px 4px;

		&-header {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 7px 12px 7px 8px;
			border-radius: 8px;
			font-weight: 500;
			margin-top: 4px;
			cursor: pointer;

			&--selectable {
				cursor: pointer;
			}

			&--disabled {
				color: rgba(0, 0, 0, 0.25);
				background-color: #f5f5f5;
				cursor: not-allowed;
			}

			&--focused {
				background-color: rgba(#3F884F, 0.05);
			}
		}

		&-title {
			flex: 1;
			font-size: 14px;
			line-height: 18px;
			color: #4D4D4D;
			font-weight: 600;
		}

		&-count {
			margin-left: 4px;
			color: #4D4D4D;
			font-size: 12px;
			font-weight: normal;
		}

		&-children {
			margin-left: 24px;
		}
	}

	&__option {
		padding: 7px 12px;
		border-radius: 8px;
		cursor: pointer;
		color: #333;
		transition: background-color 0.2s ease;

		&:hover {
			background-color: rgba(#3F884F, 0.05);
		}

		&--focused {
			background-color: rgba(#3F884F, 0.05);
		}

		&--select-all {
			&:hover {
				background-color: transparent;
			}
		}

		&--disabled {
			color: rgba(0, 0, 0, 0.25);
			cursor: not-allowed;

			&:hover {
				background-color: transparent;
			}
		}

		&--child {
			padding-left: 24px !important;
			font-size: 14px;
			line-height: 18px;
			color: #333;
		}

		&-label {
			display: flex;
			gap: 8px;
			cursor: pointer;
			width: 100%;
			user-select: none;

			span {
				font-size: 14px;
				line-height: 18px;
				color: #333;
				flex: 1;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}
		}
	}

	&__empty {
		padding: 12px;
		text-align: center;
		color: #999;
	}

	&-dropdown-enter-active,
	&-dropdown-leave-active {
		transition: opacity 0.2s ease, transform 0.2s ease;
	}

	&-dropdown-enter-from,
	&-dropdown-leave-to {
		opacity: 0;
		transform: translateY(-8px);
	}
}
</style>
